import React, { useEffect } from 'react';
import type { Env } from '../shared';
import { useAreaBroadcastsList, AreaBroadcastRow } from './api';

interface HistoryTableProps {
  env: Env;
  refreshKey?: number;
  // When provided, skips the internal fetch and renders the supplied items.
  // Lets parents share a single broadcasts fetch across the table, stats,
  // and trend chart.
  items?: AreaBroadcastRow[];
  loading?: boolean;
  error?: string | null;
}

function TruncatedText({ text, maxChars = 30, title }: { text: string; maxChars?: number; title?: string }) {
  const display = text.length > maxChars ? text.slice(0, maxChars) + '…' : text;
  return (
    <span title={title || text}>
      {display}
    </span>
  );
}

function StatusBadge({ status }: { status: 'queued' | 'completed' | 'failed' }) {
  const badgeConfig = {
    queued:    { bg: 'bg-amber-500/10', border: 'border-amber-700/30', text: 'text-amber-400', label: 'Queued' },
    completed: { bg: 'bg-green-500/10', border: 'border-green-700/30', text: 'text-green-400', label: 'Completed' },
    failed:    { bg: 'bg-red-500/10',   border: 'border-red-700/30',   text: 'text-red-400',   label: 'Failed' },
  };
  const config = badgeConfig[status];
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${config.bg} ${config.border} ${config.text}`}>
      {config.label}
    </span>
  );
}

export function HistoryTable(props: HistoryTableProps): React.ReactElement {
  const { env, refreshKey = 0, items: externalItems, loading: externalLoading, error: externalError } = props;
  const useExternal = externalItems !== undefined;

  const fetched = useAreaBroadcastsList({ env, limit: 50, skip: useExternal });

  const items = useExternal ? externalItems! : fetched.items;
  const loading = useExternal ? !!externalLoading : fetched.loading;
  const error = useExternal ? (externalError ?? null) : fetched.error;
  const refetch = fetched.refetch;

  useEffect(() => {
    if (!useExternal) refetch();
  }, [refreshKey, refetch, useExternal]);

  // ── Loading state ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="px-4 py-8 text-center">
        <p className="text-sm text-gray-400">Loading…</p>
      </div>
    );
  }

  // ── Error state ──────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="px-4 py-8 text-center">
        <p className="text-sm text-red-400">Error: {error}</p>
      </div>
    );
  }

  // ── Empty state ──────────────────────────────────────────────────────────
  if (items.length === 0) {
    return (
      <div className="px-4 py-8 text-center">
        <p className="text-sm text-gray-500">No broadcasts yet.</p>
      </div>
    );
  }

  // ── Table ────────────────────────────────────────────────────────────────
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-gray-800">
          <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">When</th>
          <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Sender</th>
          <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
          <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Area</th>
          <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Audience</th>
          <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Delivered</th>
          <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Opens</th>
          <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
        </tr>
      </thead>
      <tbody>
        {items.map((row, i) => (
          <tr
            key={row.id}
            className={`${i < items.length - 1 ? 'border-b border-gray-800/60' : ''} hover:bg-gray-800/20 transition-colors`}
          >
            <td className="px-4 py-3.5 text-gray-300">
              <span className="text-xs">
                {new Date(row.created_at).toLocaleString()}
              </span>
            </td>
            <td className="px-4 py-3.5 text-gray-300">
              <TruncatedText text={row.admin_sub} maxChars={20} />
            </td>
            <td className="px-4 py-3.5 text-gray-300">
              <TruncatedText text={row.title} maxChars={30} title={row.body || ''} />
            </td>
            <td className="px-4 py-3.5 text-gray-300">
              <span className="text-xs">
                {row.market_id ? 'Market' : 'Custom'} · {row.radius_mi.toFixed(1)} mi
              </span>
            </td>
            <td className="px-4 py-3.5 text-right text-gray-300">
              <span className="text-sm font-medium">
                {row.audience_count.toLocaleString()}
              </span>
            </td>
            <td className="px-4 py-3.5 text-right text-gray-300">
              <span className="text-sm font-medium">
                {row.delivered_count !== null ? row.delivered_count.toLocaleString() : '—'}
              </span>
            </td>
            <td className="px-4 py-3.5 text-right text-gray-300">
              <span className="text-sm font-medium">
                {(row.opened_count ?? 0).toLocaleString()}
              </span>
            </td>
            <td className="px-4 py-3.5">
              <StatusBadge status={row.status} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
